import { useEffect, useState } from 'react';
import { autoPlacement, autoUpdate, offset, useDismiss, useFloating, useInteractions } from '@floating-ui/react';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { logError } from 'services/logging/logging';
import { WebEDIError } from 'services/webediErrors/types';
import { GenericError } from 'support/helpers/errors/errors';
import { Alert } from 'components/Display/Alert/Alert';
import { Translate } from 'components/Translate/Translate';

type MessageNonFieldErrorBannerProps = {
  error: WebEDIError;
  sectionDescription: string | undefined;
  totalNumberOfErrors: number;
  setCurrentError: (error: WebEDIError) => void;
};

export const MessageNonFieldErrorBanner = ({
  error,
  sectionDescription,
  totalNumberOfErrors,
  setCurrentError,
}: MessageNonFieldErrorBannerProps) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { ErrorModalComponent } = useErrorContext();
  const { refs, floatingStyles, context } = useFloating({
    open: showErrorModal,
    onOpenChange: setShowErrorModal,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      autoPlacement({ allowedPlacements: ['bottom-start', 'bottom', 'bottom-end', 'top-start', 'top', 'top-end'] }),
    ],
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    if (totalNumberOfErrors === 0 && error) {
      logError(new GenericError('MessageNonFieldErrorBanner: No errors found when rendering the banner', error));
    }
  }, [error, totalNumberOfErrors]);

  useEffect(() => {
    if (error && showErrorModal) {
      setCurrentError(error);
    }
  }, [error, setCurrentError, showErrorModal]);

  if (totalNumberOfErrors === 0) {
    return null;
  }

  return (
    <>
      <Alert
        severity="error"
        actions={{
          alignment: 'right',
          primary: {
            variant: 'minimal-danger',
            children: <Translate i18nKey="webedi:MessageNonFieldErrorBanner.cta" />,
            onFocus: () => setShowErrorModal(true),
            onBlur: () => setShowErrorModal(false),
            size: 'extra-small',
            ref: refs.setReference,
            ...getReferenceProps(),
            'data-field': error.id,
          } as any,
        }}
      >
        {error.title}
      </Alert>

      {showErrorModal && ErrorModalComponent ? (
        <div {...getFloatingProps()}>
          <ErrorModalComponent
            viewMode={false}
            error={error}
            numberOfErrors={totalNumberOfErrors}
            ref={refs.setFloating}
            style={floatingStyles}
            fieldDescription={sectionDescription}
          />
        </div>
      ) : null}
    </>
  );
};
